import { FormEvent, useEffect, useRef, useState } from 'react';
import { fetchMyProfile } from '../../api/auth';
import {
  fetchCompanyInventory,
  fetchCompanyInventoryMasters,
} from '../../api/companyInventory';
import {
  createFinalInspection,
  deleteFinalInspectionAttachment,
  fetchFinalInspection,
  fetchFinalInspections,
  updateFinalInspection,
  uploadFinalInspectionAttachment,
} from '../../api/finalInspection';
import {
  CompanyInventoryItem,
  CompanyInventoryMaster,
} from '../../types/companyInventory';
import {
  FinalInspectionAttachment,
  FinalInspectionItem,
  FinalInspectionPayload,
  finalInspectionStatusLabel,
} from '../../types/finalInspection';
import { INSPECTION_FORM_TYPE_FINAL } from '../../types/inspectionForm';
import {
  defaultStaticForm,
  listStaticForms,
  StaticInspectionForm,
} from '../../data/staticInspectionForms';
import CompanyInventoryPickerModal from './CompanyInventoryPickerModal';
import FinalInspectionSheet, { FinalSheetValues } from './FinalInspectionSheet';
import MidProductionLotPicker from './MidProductionLotPicker';
import PreviousInspectionPickerModal from './PreviousInspectionPickerModal';
import { notifyError, reportError } from '../../utils/notify';
import './AcceptanceInspectionCreate.css';
import './ProductionDailyReportPanel.css';

interface Props {
  inspectionId?: number;
  onCancel: () => void;
  onSaved: (inspectionId: number) => void;
  contractId?: number | null;
}

const emptyItem = (): FinalInspectionItem => ({
  itemName: '',
  standardText: '',
  methodText: '',
  sample1: '',
  sample2: '',
  sample3: '',
  sample4: '',
  sample5: '',
  judgment: '',
});

const initialSheet: FinalSheetValues = {
  inventoryId: null,
  itemKind: '',
  itemName: '',
  specText: '',
  authorName: '',
  lotNo: '',
  lotQty: '',
  inspectorName: '',
  inspectionDate: new Date().toISOString().slice(0, 10),
  overallJudgment: '',
  remark: '',
  items: [],
};

function FinalInspectionCreate({
  inspectionId,
  onCancel,
  onSaved,
  contractId = null,
}: Props) {
  const isEdit = inspectionId != null;
  const forms = listStaticForms(INSPECTION_FORM_TYPE_FINAL);
  const [inventory, setInventory] = useState<CompanyInventoryItem[]>([]);
  const [masters, setMasters] = useState<CompanyInventoryMaster[]>([]);
  const [formKey, setFormKey] = useState<string | null>(() => defaultStaticForm(INSPECTION_FORM_TYPE_FINAL).key);
  const [sheet, setSheet] = useState(initialSheet);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [lotNoHint, setLotNoHint] = useState(
    '저장 시 자동 발급됩니다. 중간검사 Lot을 선택하면 그 값이 사용됩니다.'
  );
  const [pendingRemarkFiles, setPendingRemarkFiles] = useState<File[]>([]);
  const [savedAttachments, setSavedAttachments] = useState<FinalInspectionAttachment[]>([]);
  const [previousRows, setPreviousRows] = useState<
    Awaited<ReturnType<typeof fetchFinalInspections>>
  >([]);
  const [showLoadPanel, setShowLoadPanel] = useState(false);
  const [loadingPrevious, setLoadingPrevious] = useState(false);
  const [showMidLotPicker, setShowMidLotPicker] = useState(false);
  const [modelPickerOpen, setModelPickerOpen] = useState(false);
  const authorNameRef = useRef('');

  const selectedForm: StaticInspectionForm | null =
    forms.find((f) => f.key === formKey) ?? null;

  const selectedInventory =
    sheet.inventoryId != null
      ? inventory.find((row) => row.inventoryId === sheet.inventoryId) || {
          inventoryId: sheet.inventoryId,
          itemKind: sheet.itemKind || 'FINISHED',
          itemName: sheet.itemName || '모델',
          specText: sheet.specText || '',
          stockQty: 0,
        }
      : null;

  const applyModel = (item: CompanyInventoryItem) => {
    if (item.itemKind !== 'FINISHED') {
      notifyError('완제품만 선택할 수 있습니다.');
      return;
    }
    setSheet((prev) => ({
      ...prev,
      inventoryId: item.inventoryId,
      itemKind: item.itemKind,
      itemName: item.itemName,
      specText: item.specText ?? '',
    }));
  };

  const selectForm = (key: string) => {
    setFormKey(key);
    if (isEdit) return;
    const form = forms.find((f) => f.key === key);
    setSheet((prev) => ({
      ...prev,
      items:
        form && form.items.length > 0
          ? form.items.map((item) => ({
              ...emptyItem(),
              itemName: item.itemName ?? '',
              standardText: item.standardText ?? '',
              methodText: item.methodText ?? '',
            }))
          : [],
    }));
  };

  useEffect(() => {
    void (async () => {
      try {
        setLoading(true);
        const [stock, masterRows, history] = await Promise.all([
          fetchCompanyInventory(),
          fetchCompanyInventoryMasters(),
          fetchFinalInspections().catch(() => []),
        ]);
        setInventory(stock);
        setMasters(masterRows);
        setPreviousRows(history);

        if (isEdit && inspectionId != null) {
          const detail = await fetchFinalInspection(inspectionId);
          if (detail.status === 'APPROVED') {
            notifyError('승인 완료된 제품검사는 수정할 수 없습니다.');
            return;
          }
          const matched =
            forms.find((f) => f.title === (detail.formTitle ?? '')) ?? forms[0] ?? null;
          if (matched) setFormKey(matched.key);
          setSheet({
            inventoryId: detail.inventoryId ?? null,
            itemKind: detail.itemKind ?? '',
            itemName: detail.itemName ?? '',
            specText: detail.specText ?? '',
            authorName: detail.authorName || authorNameRef.current,
            lotNo: detail.lotNo ?? '',
            lotQty: detail.lotQty ?? '',
            inspectorName: detail.inspectorName ?? authorNameRef.current,
            inspectionDate:
              detail.inspectionDate?.slice(0, 10) ?? new Date().toISOString().slice(0, 10),
            overallJudgment: detail.overallJudgment ?? '',
            remark: detail.remark ?? '',
            items:
              (detail.items ?? []).length > 0
                ? (detail.items ?? []).map((item) => ({
                    itemName: item.itemName ?? '',
                    standardText: item.standardText ?? '',
                    methodText: item.methodText ?? '',
                    sample1: item.sample1 ?? '',
                    sample2: item.sample2 ?? '',
                    sample3: item.sample3 ?? '',
                    sample4: item.sample4 ?? '',
                    sample5: item.sample5 ?? '',
                    judgment: item.judgment ?? '',
                  }))
                : [],
          });
          setSavedAttachments(detail.attachments ?? []);
          setPendingRemarkFiles([]);
          setLotNoHint('중간검사 Lot을 다시 고르거나 값을 수정할 수 있습니다.');
        } else if (forms[0]) {
          selectForm(forms[0].key);
        }
        setError('');
      } catch {
        reportError(
          setError,
          isEdit
            ? '제품검사 수정 정보를 불러오지 못했습니다.'
            : '제품검사 등록 정보를 불러오지 못했습니다.'
        );
      } finally {
        setLoading(false);
      }
    })();
    void fetchMyProfile()
      .then((profile) => {
        const name = profile.nickname?.trim() || profile.loginId?.trim() || '';
        authorNameRef.current = name;
        setSheet((prev) => ({
          ...prev,
          authorName: prev.authorName || name,
          inspectorName: prev.inspectorName || name,
        }));
      })
      .catch(() => undefined);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- mount / edit id
  }, [inspectionId, isEdit]);

  const applyPrevious = async (rowId: number) => {
    try {
      setLoadingPrevious(true);
      setError('');
      const detail = await fetchFinalInspection(rowId);
      const matched =
        forms.find((f) => f.title === (detail.formTitle ?? '')) ?? forms[0] ?? null;
      if (matched) setFormKey(matched.key);
      setPendingRemarkFiles([]);
      if (!isEdit) setSavedAttachments([]);
      setSheet((prev) => ({
        ...prev,
        inventoryId: detail.inventoryId ?? null,
        itemKind: detail.itemKind ?? '',
        itemName: detail.itemName ?? '',
        specText: detail.specText ?? '',
        authorName: isEdit
          ? detail.authorName || prev.authorName
          : authorNameRef.current || prev.authorName,
        lotNo: isEdit ? detail.lotNo ?? '' : '',
        lotQty: detail.lotQty ?? '',
        inspectorName: detail.inspectorName ?? authorNameRef.current,
        inspectionDate: new Date().toISOString().slice(0, 10),
        overallJudgment: detail.overallJudgment ?? '',
        remark: detail.remark ?? '',
        items:
          (detail.items ?? []).length > 0
            ? (detail.items ?? []).map((item) => ({
                itemName: item.itemName ?? '',
                standardText: item.standardText ?? '',
                methodText: item.methodText ?? '',
                sample1: item.sample1 ?? '',
                sample2: item.sample2 ?? '',
                sample3: item.sample3 ?? '',
                sample4: item.sample4 ?? '',
                sample5: item.sample5 ?? '',
                judgment: item.judgment ?? '',
              }))
            : [],
      }));
      setShowLoadPanel(false);
    } catch {
      notifyError('이전 제품검사를 불러오지 못했습니다.');
    } finally {
      setLoadingPrevious(false);
    }
  };

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (!sheet.inventoryId) {
      notifyError('재고 모델을 선택해 주세요.');
      return;
    }
    if (!selectedForm) {
      notifyError('제품검사 양식을 선택해 주세요.');
      return;
    }
    if (!sheet.overallJudgment.trim()) {
      notifyError('종합판정은 필수입니다.');
      return;
    }
    const payload: FinalInspectionPayload = {
      inventoryId: sheet.inventoryId,
      contractId: contractId ?? undefined,
      formTitle: selectedForm.title,
      status: 'COMPLETED',
      authorName: sheet.authorName || undefined,
      lotNo: sheet.lotNo || undefined,
      lotQty: sheet.lotQty || undefined,
      inspectorName: sheet.inspectorName || undefined,
      inspectionDate: sheet.inspectionDate || null,
      overallJudgment: sheet.overallJudgment.trim(),
      remark: sheet.remark || undefined,
      items: sheet.items,
    };
    try {
      setSaving(true);
      setError('');
      if (isEdit && inspectionId != null) {
        const updated = await updateFinalInspection(inspectionId, payload);
        for (const file of pendingRemarkFiles) {
          await uploadFinalInspectionAttachment(updated.inspectionId, file);
        }
        onSaved(updated.inspectionId);
      } else {
        const created = await createFinalInspection(payload);
        for (const file of pendingRemarkFiles) {
          await uploadFinalInspectionAttachment(created.inspectionId, file);
        }
        onSaved(created.inspectionId);
      }
    } catch {
      notifyError(isEdit ? '제품검사 수정에 실패했습니다.' : '제품검사 등록에 실패했습니다.');
    } finally {
      setSaving(false);
    }
  };

  const updateItem = (index: number, patch: Partial<FinalInspectionItem>) =>
    setSheet((prev) => ({
      ...prev,
      items: prev.items.map((item, i) => (i === index ? { ...item, ...patch } : item)),
    }));

  if (loading) {
    return (
      <div className="aic">
        <p className="aic__hint">불러오는 중…</p>
      </div>
    );
  }

  return (
    <div className="aic">
      <div className="aic__header">
        <div>
          <h3 className="aic__title">{isEdit ? '제품검사 수정' : '제품검사 등록'}</h3>
          <p className="aic__subtitle">
            모델을 고른 뒤 중간검사 생산 Lot을 불러와 최종 검사 결과를 입력하세요.
          </p>
        </div>
        <div className="aic__actions">
          <button
            type="button"
            className="aic__btn is-ghost"
            disabled={previousRows.length === 0}
            onClick={() => setShowLoadPanel(true)}
          >
            이전 작성 불러오기
          </button>
          <button type="button" className="aic__btn is-ghost" onClick={onCancel}>
            취소
          </button>
        </div>
      </div>
      {error ? <p className="aic__error">{error}</p> : null}

      <PreviousInspectionPickerModal
        open={showLoadPanel}
        title="이전 제품검사 선택"
        emptyText="이전 제품검사가 없습니다."
        loading={loadingPrevious}
        columns={[
          { key: 'item', label: '모델' },
          { key: 'form', label: '양식' },
          { key: 'lot', label: 'Lot' },
          { key: 'date', label: '검사일' },
          { key: 'status', label: '상태' },
        ]}
        rows={previousRows.map((row) => ({
          id: row.inspectionId,
          cells: {
            item: row.itemName?.trim() || '-',
            form: row.formTitle || '-',
            lot: row.lotNo || '-',
            date: row.inspectionDate?.slice(0, 10) || '-',
            status: finalInspectionStatusLabel(row.status),
          },
        }))}
        onClose={() => setShowLoadPanel(false)}
        onSelect={applyPrevious}
      />

      {showMidLotPicker && selectedInventory ? (
        <MidProductionLotPicker
          inventory={selectedInventory}
          onClose={() => setShowMidLotPicker(false)}
          onSelect={(pick) => {
            setSheet((prev) => ({
              ...prev,
              inventoryId: pick.inventoryId || prev.inventoryId,
              itemName: pick.itemName || prev.itemName,
              specText: pick.specText || prev.specText,
              lotNo: pick.lotNo,
              lotQty: pick.lotQty,
            }));
            setLotNoHint(
              `중간검사 #${pick.midInspectionId}에서 가져옴 · Lot ${pick.lotNo}${
                pick.lotQty ? ` · 수량 ${pick.lotQty}` : ''
              }`
            );
            setShowMidLotPicker(false);
          }}
        />
      ) : null}

      <section className="aic__card">
        <div className="aic__card-head">
          <h4 className="aic__step">
            <span className="aic__step-num">1</span>제품검사 양식
          </h4>
          <p className="aic__hint">화면 고정 양식입니다. 나중에 템플릿을 추가하면 여기서 고를 수 있습니다.</p>
        </div>
        <select value={formKey ?? ''} onChange={(e) => selectForm(e.target.value)}>
          {forms.map((form) => (
            <option key={form.key} value={form.key}>
              {form.title}
            </option>
          ))}
        </select>
      </section>
      <CompanyInventoryPickerModal
        open={modelPickerOpen}
        items={inventory.filter((row) => row.itemKind === 'FINISHED')}
        masters={masters.filter((row) => row.itemKind === 'FINISHED')}
        initialKind="FINISHED"
        title="검사 대상 모델 선택"
        onClose={() => setModelPickerOpen(false)}
        onListsUpdated={(nextInventory, nextMasters) => {
          setInventory(nextInventory);
          setMasters(nextMasters);
        }}
        onSelect={applyModel}
      />
      {selectedForm ? (
        <form onSubmit={(e) => void submit(e)}>
          <div className="aic__footer">
            <button type="button" className="aic__btn is-ghost" onClick={onCancel}>
              취소
            </button>
            <button type="submit" className="aic__btn is-primary" disabled={saving}>
              {saving ? '저장 중…' : '저장'}
            </button>
          </div>
          <section className="aic__card">
            <FinalInspectionSheet
              lotNoHint={lotNoHint}
              editable
              values={sheet}
              inventoryOptions={inventory}
              remarkAttachments={savedAttachments}
              pendingRemarkFiles={pendingRemarkFiles}
              onChange={(patch) => setSheet((prev) => ({ ...prev, ...patch }))}
              onItemChange={updateItem}
              onAddItem={() =>
                setSheet((prev) => ({ ...prev, items: [...prev.items, emptyItem()] }))
              }
              onRemoveItem={(index) =>
                setSheet((prev) => ({
                  ...prev,
                  items: prev.items.filter((_, i) => i !== index),
                }))
              }
              onOpenModelPicker={() => setModelPickerOpen(true)}
              onOpenMidLotPicker={() => {
                if (!sheet.inventoryId) {
                  notifyError('모델을 먼저 선택해 주세요.');
                  return;
                }
                setShowMidLotPicker(true);
              }}
              onPickRemarkFiles={(files) => setPendingRemarkFiles((prev) => [...prev, ...files])}
              onRemovePendingRemarkFile={(index) =>
                setPendingRemarkFiles((prev) => prev.filter((_, i) => i !== index))
              }
              onDeleteAttachment={(file) => {
                if (!isEdit || inspectionId == null) return;
                if (!window.confirm(`"${file.fileName}" 첨부를 삭제할까요?`)) return;
                void deleteFinalInspectionAttachment(inspectionId, file.attachmentId)
                  .then(() =>
                    setSavedAttachments((prev) =>
                      prev.filter((row) => row.attachmentId !== file.attachmentId)
                    )
                  )
                  .catch(() => reportError(setError, '첨부파일 삭제에 실패했습니다.'));
              }}
            />
          </section>
          <div className="aic__footer">
            <button type="button" className="aic__btn is-ghost" onClick={onCancel}>
              취소
            </button>
            <button type="submit" className="aic__btn is-primary" disabled={saving}>
              {saving ? '저장 중…' : '저장'}
            </button>
          </div>
        </form>
      ) : null}
    </div>
  );
}

export default FinalInspectionCreate;
